import {formatPercentage} from "@/adapters/push.adapter";
import {BaseBadge} from "@/components/BaseBadge";
import {BaseButton} from "@/components/BaseButton";
import {BaseCard} from "@/components/BaseCard";
import {PATHS} from "@/constants/routes";
import {useCampaigns} from "@/hooks/usePush";
import type {CampaignFilters as CampaignFiltersType} from "@/types/push.types";
import {formatDistanceToNow} from "@/utils/date.utils";
import {Eye, Plus} from "lucide-react";
import {useState} from "react";
import {useNavigate} from "react-router";
import {CampaignFilters} from "./components/CampaignFilters";

export const CampaignHistory = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<CampaignFiltersType>({
    limit: 20,
    offset: 0,
  });

  const {data, isLoading} = useCampaigns(filters);

  const handleFilterChange = (newFilters: Partial<CampaignFiltersType>) => {
    setFilters(prev => ({...prev, ...newFilters, offset: 0}));
  };

  const handlePageChange = (newOffset: number) => {
    setFilters(prev => ({...prev, offset: newOffset}));
  };

  const formatDate = (iso: string) => {
    try {
      return formatDistanceToNow(new Date(iso));
    } catch {
      return "—";
    }
  };

  const campaigns = data?.data ?? [];
  const pagination = data?.pagination;

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1
            className="text-2xl font-bold"
            style={{color: "var(--color-text)"}}
          >
            Campaign History
          </h1>
          <p
            className="text-sm mt-1"
            style={{color: "var(--color-text-secondary)"}}
          >
            View and manage all push notification campaigns
          </p>
        </div>

        <BaseButton
          variant="primary"
          onClick={() => navigate(PATHS.PUSH_CAMPAIGN_CREATE)}
          leftIcon={<Plus size={18} />}
        >
          New Campaign
        </BaseButton>
      </div>

      {/* Filters */}
      <CampaignFilters
        filters={filters}
        onChange={handleFilterChange}
      />

      {/* Table */}
      <BaseCard padding="none">
        {isLoading ? (
          <div className="p-8 text-center">
            <p
              className="text-sm"
              style={{color: "var(--color-text-secondary)"}}
            >
              Loading campaigns...
            </p>
          </div>
        ) : campaigns.length === 0 ? (
          <div className="p-8 text-center">
            <p
              className="text-sm"
              style={{color: "var(--color-text-secondary)"}}
            >
              No campaigns found
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{borderBottom: "1px solid var(--color-surface-2)"}}>
                  <th
                    className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide"
                    style={{color: "var(--color-text-secondary)"}}
                  >
                    Title
                  </th>
                  <th
                    className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide"
                    style={{color: "var(--color-text-secondary)"}}
                  >
                    Category
                  </th>
                  <th
                    className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide"
                    style={{color: "var(--color-text-secondary)"}}
                  >
                    Status
                  </th>
                  <th
                    className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wide"
                    style={{color: "var(--color-text-secondary)"}}
                  >
                    Recipients
                  </th>
                  <th
                    className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wide"
                    style={{color: "var(--color-text-secondary)"}}
                  >
                    Delivered
                  </th>
                  <th
                    className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wide"
                    style={{color: "var(--color-text-secondary)"}}
                  >
                    Read Rate
                  </th>
                  <th
                    className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide"
                    style={{color: "var(--color-text-secondary)"}}
                  >
                    Sent
                  </th>
                  <th className="w-20 py-3 px-4" />
                </tr>
              </thead>
              <tbody>
                {campaigns.map(campaign => (
                  <tr
                    key={campaign.id}
                    className="group transition-colors cursor-pointer"
                    style={{borderBottom: "1px solid var(--color-surface-2)"}}
                    onMouseEnter={e => {
                      e.currentTarget.style.backgroundColor = "var(--color-surface-2)";
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <td className="py-3 px-4">
                      <p
                        className="text-sm font-medium"
                        style={{color: "var(--color-text)"}}
                      >
                        {campaign.title}
                      </p>
                      <p
                        className="text-xs mt-0.5 truncate max-w-md"
                        style={{color: "var(--color-text-secondary)"}}
                      >
                        {campaign.body}
                      </p>
                    </td>
                    <td className="py-3 px-4">
                      <BaseBadge
                        label={campaign.category}
                        variant="neutral"
                      />
                    </td>
                    <td className="py-3 px-4">
                      <BaseBadge
                        label={campaign.status}
                        variant={
                          campaign.status === "sent"
                            ? "success"
                            : campaign.status === "failed"
                              ? "danger"
                              : campaign.status === "sending"
                                ? "warning"
                                : "neutral"
                        }
                      />
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span
                        className="text-sm"
                        style={{color: "var(--color-text)"}}
                      >
                        {campaign.totalRecipients.toLocaleString()}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span
                        className="text-sm"
                        style={{color: "var(--color-text)"}}
                      >
                        {campaign.totalDelivered.toLocaleString()}
                      </span>
                      <span
                        className="text-xs ml-1"
                        style={{color: "var(--color-text-secondary)"}}
                      >
                        ({formatPercentage(campaign.deliveryRate)})
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span
                        className="text-sm font-semibold"
                        style={{
                          color: campaign.readRate > 50 ? "var(--color-success)" : "var(--color-text)",
                        }}
                      >
                        {formatPercentage(campaign.readRate)}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className="text-sm"
                        style={{color: "var(--color-text-secondary)"}}
                      >
                        {campaign.sentAt ? formatDate(campaign.sentAt) : "—"}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <BaseButton
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(PATHS.PUSH_CAMPAIGN_DETAIL(campaign.id))}
                        leftIcon={<Eye size={16} />}
                      >
                        View
                      </BaseButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.total > 0 && (
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{borderTop: "1px solid var(--color-surface-2)"}}
          >
            <p
              className="text-sm"
              style={{color: "var(--color-text-secondary)"}}
            >
              Showing {(filters.offset ?? 0) + 1} -{" "}
              {Math.min((filters.offset ?? 0) + (filters.limit ?? 20), pagination.total)} of{" "}
              {pagination.total.toLocaleString()} campaigns
            </p>

            <div className="flex gap-2">
              <BaseButton
                variant="ghost"
                size="sm"
                disabled={!pagination.hasMore || (filters.offset ?? 0) === 0}
                onClick={() => handlePageChange(Math.max(0, (filters.offset ?? 0) - (filters.limit ?? 20)))}
              >
                Previous
              </BaseButton>
              <BaseButton
                variant="ghost"
                size="sm"
                disabled={!pagination.hasMore}
                onClick={() => handlePageChange((filters.offset ?? 0) + (filters.limit ?? 20))}
              >
                Next
              </BaseButton>
            </div>
          </div>
        )}
      </BaseCard>
    </div>
  );
};
